import { IonItem, IonItemSliding } from "@ionic/react";
import { AnimatePresence } from "framer-motion";
import { useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import Collapse from "@components/animations/Collapse";
import styles from "@components/carePlan/CarePlan.module.css";
import CarePlanActionItem from "@components/carePlan/CarePlanActionItem";
import GoalPopover from "@components/carePlan/GoalPopover";
import CarePlanCard from "@components/CarePlanCard";
import ClientAvatar from "@components/ClientAvatar";
import ConditionalTooltip from "@components/ConditionalTooltip";
import ExpertAvatar from "@components/ExpertAvatar";
import CarePlanActionItemForm from "@components/forms/CarePlanActionItemForm";
import Tag from "@components/labels/Tag";
import Modal, { ModalElement } from "@components/Modal";
import SlidingItemOptions from "@components/SlidingItemOptions";
import Icon from "@components/text/Icon";
import BodyCopy from "@components/typography/BodyCopy";
import ButtonLink from "@components/typography/ButtonLink";
import Heading2 from "@components/typography/Heading2";
import InternalLink from "@components/typography/InternalLink";
import useDestroyWorksheet from "@hooks/mutations/useDestroyWorksheet";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyClient from "@hooks/useMyClient";
import useOpener from "@hooks/useOpener";
import { CarePlanSectionEnum, JourneyStatusEnum } from "@typing/Enums";
import {
  CarePlanActionItemFragment,
  ExpertRoleRoleEnum,
  GetJourneyWithPlanQuery,
  GoalItemRemoveDocument,
  GoalItemRemoveMutation,
  GoalItemRemoveMutationVariables,
  GoalItemToggleCompletedDocument,
  GoalItemToggleCompletedMutation,
  GoalItemToggleCompletedMutationVariables,
  GoalStatusEnum,
  GoalUpdateDocument,
  GoalUpdateMutation,
  GoalUpdateMutationVariables,
  WorksheetUpdateDocument,
  WorksheetUpdateMutation,
  WorksheetUpdateMutationVariables
} from "@typing/Generated";
import { Direction } from "@typing/Models";
import { orderedTasks, sortByTimestamp } from "@utils/modelUtils";
import { isMobile } from "@utils/platformUtils";
import { journeyRoute } from "@utils/routeUtils";

type Task = GetJourneyWithPlanQuery["journey"]["goals"][0]["tasks"][0];

type Props = {
  completed: boolean;
  goal: GetJourneyWithPlanQuery["journey"]["goals"][0];
  handleCurrentSectionChange: (section: CarePlanSectionEnum, direction: Direction, worksheetId?: string) => void;
  journey: GetJourneyWithPlanQuery["journey"];
  setIsExploding: (isExploading: boolean) => void;
};
const Goal = ({ completed, goal, handleCurrentSectionChange, journey, setIsExploding }: Props) => {
  const intl = useIntl();
  const myClient = useMyClient();
  const actionItemModalRef = useRef<ModalElement>(null);
  const actionItemOpener = useOpener();

  const primaryExpert = journey.expertRoles?.find(role => role.role === ExpertRoleRoleEnum.PRIMARY)?.expert;

  const [submitting, setSubmitting] = useState(false);
  const [actionItemBeingEdited, setActionItemBeingEdited] = useState<CarePlanActionItemFragment | undefined>(undefined);

  const tasks = orderedTasks(goal.tasks).filter(task => task.clientCompleted === completed || goal.completed);

  const orderedActionItems = sortByTimestamp(goal.carePlanActionItems, a => a.createdAt).filter(
    actionItem => actionItem.completed === completed || goal.completed
  );

  const orderedWorksheets = sortByTimestamp(goal.worksheets, w => w.createdAt).filter(
    worksheet => worksheet.completed === completed || goal.completed
  );

  const orderedGoalItems = sortByTimestamp(goal.goalItems, gi => gi.createdAt).filter(
    goalItem => goalItem.completed === completed || goal.completed
  );

  const onActionItemEdit = (actionItem: CarePlanActionItemFragment) => {
    setActionItemBeingEdited(actionItem);
    actionItemOpener.open();
  };

  const [goalItemRemove] = useMutationWithValidation<GoalItemRemoveMutation, GoalItemRemoveMutationVariables>(
    GoalItemRemoveDocument,
    "goalItem"
  );

  const handleActionItemError = () => {
    if (actionItemModalRef.current) {
      actionItemModalRef.current.scrollToTop();
    }
  };

  const [updateWorksheet] = useMutationWithValidation<WorksheetUpdateMutation, WorksheetUpdateMutationVariables>(
    WorksheetUpdateDocument,
    "worksheet"
  );

  const [toggleGoalItemCompleted] = useMutationWithValidation<
    GoalItemToggleCompletedMutation,
    GoalItemToggleCompletedMutationVariables
  >(GoalItemToggleCompletedDocument, "goalItem");

  const [goalToggleCompleted] = useMutationWithValidation<GoalUpdateMutation, GoalUpdateMutationVariables>(
    GoalUpdateDocument,
    "goal",
    {
      update: store => {
        store.modify({
          fields: {
            completed() {
              return !goal.completed;
            }
          },
          id: store.identify({ ...goal })
        });
      }
    }
  );

  const getTaskStatus = (task: Task) => {
    if (!task.completed) {
      return JourneyStatusEnum.IN_PROGRESS;
    } else if (task.goal?.status === GoalStatusEnum.EDIT_MODE || task.goal?.hasDuplicateGoalInEditMode) {
      return JourneyStatusEnum.UPDATING;
    }
    return JourneyStatusEnum.COMPLETED;
  };

  const getColorForStatus = (status: JourneyStatusEnum, tint: boolean = false) => {
    switch (status) {
      case JourneyStatusEnum.COMPLETED:
        return tint ? "primary-500" : "primary";
      default:
        return tint ? "secondary-500" : "secondary";
    }
  };

  const worksheetDestroy = useDestroyWorksheet(journey);

  const handleMarkedCompleted = (itemCompleted: boolean, id: string) => {
    if (itemCompleted && !goal.completed && !completed) {
      const items = [...tasks, ...orderedActionItems, ...orderedWorksheets, ...orderedGoalItems].filter(
        item => item.id !== id
      );

      if (items.length === 0) {
        goalToggleCompleted({
          completed: true,
          id: goal.id
        }).then(() => {
          setIsExploding(true);
          setTimeout(() => {
            setIsExploding(false);
          }, 1000);
        });
      }
    }
  };

  return (
    <div className="margin-20-bottom" data-test="goal">
      <div className={`${styles.goal} flex justify-between items-center`}>
        <div className="flex gap-md items-center">
          <div className="ion-hide-lg-down">
            <Icon color="primary" icon={goal.completed ? "check" : "folder"} size="medium" />
          </div>
          <Heading2>{goal.title}</Heading2>
        </div>
        <GoalPopover goal={goal} journey={journey} setIsExploding={setIsExploding} />
      </div>
      <div className={`flex flex-column ${styles.taskGap}`}>
        <AnimatePresence>
          {tasks.map(task => {
            const status = getTaskStatus(task);
            return (
              <Collapse className="padding-3" key={task.id}>
                <CarePlanCard
                  body={
                    <>
                      <div className="flex justify-between items-center gap-sm">
                        {primaryExpert && <ExpertAvatar expert={primaryExpert} withTooltip={false} />}
                        <div className="text-ellipsis flex-grow">
                          <BodyCopy bold className="ion-hide-md-down no-wrap" color="white">
                            <FormattedMessage id={`components.Goal.tasks.status.${status}.description`} />
                          </BodyCopy>
                          <BodyCopy bold className="ion-hide-md-up no-wrap" color="white">
                            <FormattedMessage id="components.Goal.tasks.status.default.description" />
                          </BodyCopy>
                          <br className="ion-hide-md-down" />
                          <BodyCopy className="no-wrap ion-hide-md-down" color="white">
                            {task.title}
                          </BodyCopy>
                        </div>
                        <div className="flex gap-md items-center">
                          <div className="flex items-center gap-xs">
                            <Tag color={getColorForStatus(status, true)}>
                              <FormattedMessage id={`components.Goal.tasks.status.${status}.label`} />
                            </Tag>
                            {status === JourneyStatusEnum.IN_PROGRESS ? (
                              <ConditionalTooltip
                                condition={status === JourneyStatusEnum.IN_PROGRESS}
                                content={intl.formatMessage({
                                  id: "components.Goal.tasks.status.in_progress.tooltip"
                                })}
                                expand
                                initialDirection="left"
                              >
                                {tooltipTriggerProps => (
                                  <Icon
                                    {...tooltipTriggerProps}
                                    color="white"
                                    icon="questionCircle"
                                    size="medium"
                                    tabIndex={0}
                                  />
                                )}
                              </ConditionalTooltip>
                            ) : (
                              <Icon color="white" icon="chevronRight" size="medium" />
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="ion-hide-md-up margin-12-top">
                        <BodyCopy color="white">{task.title}</BodyCopy>
                      </div>
                    </>
                  }
                  color={getColorForStatus(status)}
                  popoverItems={[]}
                  routerLink={journeyRoute({
                    journeyId: journey.id,
                    linkAfterJourney: `/client-visible-tasks/${task.id}`,
                    myClient
                  })}
                />
              </Collapse>
            );
          })}
          {orderedActionItems.map(carePlanActionItem => (
            <Collapse className="padding-3" key={carePlanActionItem.id}>
              <CarePlanActionItem
                carePlanActionItem={carePlanActionItem}
                handleMarkedCompleted={handleMarkedCompleted}
                journey={journey}
                onEdit={onActionItemEdit}
              />
            </Collapse>
          ))}
          {orderedWorksheets.map(worksheet => (
            <Collapse className="padding-3" key={worksheet.id}>
              <IonItemSliding disabled={!isMobile()}>
                <IonItem className={styles.slidingItem} lines="none">
                  <CarePlanCard
                    body={
                      <div className="flex justify-between items-center gap-10">
                        <BodyCopy>
                          <FormattedMessage
                            id="models.worksheet.title"
                            values={{
                              title: isMobile() ? (
                                <ButtonLink
                                  className="bold"
                                  onClick={() =>
                                    handleCurrentSectionChange(CarePlanSectionEnum.WORKSHEET, "right", worksheet.id)
                                  }
                                  stylized={false}
                                >
                                  {worksheet.title}
                                </ButtonLink>
                              ) : (
                                <InternalLink
                                  className="bold"
                                  linkEventCreateVariables={{ journeyId: journey.id }}
                                  stylized={false}
                                  to={journeyRoute({
                                    journeyId: journey.id,
                                    linkAfterJourney: `/worksheets/${worksheet.id}`,
                                    myClient
                                  })}
                                >
                                  {worksheet.title}
                                </InternalLink>
                              )
                            }}
                          />
                        </BodyCopy>
                        {journey.clientRoles.length > 1 && (
                          <ClientAvatar
                            clientId={journey.owner.id}
                            color="primary-500"
                            firstName={journey.owner.firstName}
                            journeyId={journey.id}
                            lastName={journey.owner.lastName}
                            size="normal"
                          />
                        )}
                      </div>
                    }
                    checkboxProps={{
                      checked: worksheet.completed,
                      disabled: submitting,
                      name: "worksheetToggleCompleted",
                      onChange: v => {
                        setSubmitting(true);
                        updateWorksheet({
                          completed: v,
                          id: worksheet.id
                        }).finally(() => {
                          setSubmitting(false);
                          handleMarkedCompleted(v, worksheet.id);
                        });
                      }
                    }}
                    popoverItems={[
                      {
                        icon: "trash",
                        onClick: () => {
                          worksheetDestroy({
                            id: worksheet.id
                          });
                        },
                        textKey: "dictionary.delete"
                      }
                    ]}
                  />
                </IonItem>
                <SlidingItemOptions
                  options={[
                    {
                      color: "danger-500",
                      icon: "trash",
                      onClick: () => {
                        worksheetDestroy({
                          id: worksheet.id
                        });
                      },
                      textKey: "dictionary.delete"
                    }
                  ]}
                  side="end"
                />
              </IonItemSliding>
            </Collapse>
          ))}
          {orderedGoalItems.map(goalItem => (
            <Collapse className="padding-3" key={goalItem.id}>
              <IonItemSliding disabled={!isMobile()}>
                <IonItem className={styles.slidingItem} lines="none">
                  <CarePlanCard
                    body={
                      <div className="flex justify-between items-center gap-10">
                        <BodyCopy>
                          <FormattedMessage
                            id={
                              goalItem.resource?.__typename === "Article"
                                ? "models.article.title"
                                : goalItem.resource?.__typename === "EmployerBenefit"
                                  ? "models.employerBenefit.title"
                                  : "models.tool.title"
                            }
                            values={{
                              title: (
                                <InternalLink
                                  className="bold"
                                  linkEventCreateVariables={{ journeyId: journey.id }}
                                  stylized={false}
                                  to={journeyRoute({
                                    journeyId: journey.id,
                                    linkAfterJourney:
                                      goalItem.resource?.__typename === "Article"
                                        ? `/articles/${goalItem.resource.id}`
                                        : goalItem.resource
                                          ? `/employer-benefits/${goalItem.resource.id}`
                                          : "/care-plan?section=trackers",
                                    myClient
                                  })}
                                >
                                  {goalItem.resource?.__typename === "Article"
                                    ? goalItem.resource.title
                                    : goalItem.resource
                                      ? goalItem.resource?.vendorName
                                      : intl.formatMessage({ id: `models.actionItemCtas.${goalItem.tool}` })}
                                </InternalLink>
                              )
                            }}
                          />
                        </BodyCopy>
                        {journey.clientRoles.length > 1 && (
                          <ClientAvatar
                            clientId={journey.owner.id}
                            color="primary-500"
                            firstName={journey.owner.firstName}
                            journeyId={journey.id}
                            lastName={journey.owner.lastName}
                            size="normal"
                          />
                        )}
                      </div>
                    }
                    checkboxProps={{
                      checked: goalItem.completed,
                      disabled: submitting,
                      name: "goalItemToggleCompleted",
                      onChange: v => {
                        setSubmitting(true);
                        toggleGoalItemCompleted({
                          completed: v,
                          id: goalItem.id
                        }).finally(() => {
                          setSubmitting(false);
                          handleMarkedCompleted(v, goalItem.id);
                        });
                      }
                    }}
                    popoverItems={[
                      {
                        icon: "trash",
                        onClick: () => {
                          goalItemRemove({
                            articleId: goalItem.resource?.__typename === "Article" ? goalItem.resource.id : undefined,
                            employerBenefitId:
                              goalItem.resource?.__typename === "EmployerBenefit" ? goalItem.resource.id : undefined,
                            goalId: goal.id,
                            tool: goalItem.tool
                          });
                        },
                        textKey: "dictionary.delete"
                      }
                    ]}
                  />
                </IonItem>
                <SlidingItemOptions
                  options={[
                    {
                      color: "danger-500",
                      icon: "trash",
                      onClick: () => {
                        goalItemRemove({
                          articleId: goalItem.resource?.__typename === "Article" ? goalItem.resource.id : undefined,
                          employerBenefitId:
                            goalItem.resource?.__typename === "EmployerBenefit" ? goalItem.resource.id : undefined,
                          goalId: goal.id,
                          tool: goalItem.tool
                        });
                      },
                      textKey: "dictionary.delete"
                    }
                  ]}
                  side="end"
                />
              </IonItemSliding>
            </Collapse>
          ))}
        </AnimatePresence>
      </div>
      <Modal
        opener={actionItemOpener}
        ref={actionItemModalRef}
        title={intl.formatMessage({
          id: actionItemBeingEdited
            ? "models.carePlanActionItem.editClientTask"
            : "models.carePlanActionItem.addClientTask"
        })}
      >
        {({ modalDisplayed }) => (
          <CarePlanActionItemForm
            autofocus={modalDisplayed}
            carePlanActionItem={actionItemBeingEdited}
            goal={goal}
            journey={journey}
            onCancel={actionItemOpener.close}
            onError={handleActionItemError}
            onSuccess={actionItemOpener.close}
          />
        )}
      </Modal>
    </div>
  );
};

export default Goal;
